import { useMemo, useState, useEffect, useCallback } from "react";
import "./index.css";
import { AppShell } from "@/components/layout/AppShell";
import { ContactList } from "@/components/contacts/ContactList";
import { ChatWindow } from "@/components/chat/ChatWindow";
import { ThreadList } from "@/components/threads/ThreadList";
import { ServerSettingsPanel } from "@/components/settings/ServerSettingsPanel";
import { ProfileSettingsPanel } from "@/components/settings/ProfileSettingsPanel";
import { AccountSettingsPanel } from "@/components/settings/AccountSettingsPanel";
import { MCPServerSearchModal } from "@/components/modals/MCPServerSearchModal";
import { DbManager } from "@/pages/dev/DbManager";
import { MOCK_SERVERS, MOCK_THREADS } from "@/services/mockData";
import type { ChatMessage, LLMSettings, MCPServer, Thread } from "@/types";
import { loadServers, saveServers } from "@/services/storage";
import {
  loadThread,
  saveThread,
  loadThreadMessages,
  saveThreadMessages,
  loadProfileSettings,
  saveProfileSettings,
  type ProfileSettings,
  createThread,
  loadThreads,
  loadThreadById,
  updateThread,
} from "@/services/db";
import { loadLLMSettings, saveLLMSettings, defaultLLMSettings } from "@/services/settings";
import { useExitIntent } from "@/hooks/useExitIntent";
import { ExitIntentModal } from "./components/modals/ExitIntentModal";
import { Toaster } from "@/components/ui/sonner";
import { MCPClient } from "@/services/mcpClient";
import { toast } from "sonner";
import { ChatWindowTest } from "./components/chat/ChatWindowTest";
import Clarity from "@microsoft/clarity";
// Import TabKey type
import type { TabKey } from "@/components/settings/AccountSettingsPanel";
import { DemoRequestModal } from "@/components/modals/DemoRequestModal";
import { WishlistModal } from "@/components/modals/WishlistModal";
import { NewsletterModal } from "@/components/modals/NewsletterModal";

// Initialize Clarity analytics
// Clarity.init(process.env.CLARITY_PROJECT_ID || "");
Clarity.init("up120q5ktf");

// Allow test override via localStorage for automated testing
const getUseHapiMcp = () => {
  const override = localStorage.getItem('USE_HAPIMCP_OVERRIDE');
  if (override !== null) {
    return override === 'true';
  }
  return false; // Default to false (legacy implementation)
};

const USE_HAPIMCP = getUseHapiMcp();

export function App() {
  useEffect(() => {
    // Prevent zoom on input focus for iOS
    const preventDefault = (e: TouchEvent) => {
      if (e.touches.length > 1) {
        e.preventDefault();
      }
    };

    document.addEventListener('touchstart', preventDefault, { passive: false });
    document.addEventListener('touchmove', preventDefault, { passive: false });

    // Set viewport height for mobile
    const setViewportHeight = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    setViewportHeight();
    window.addEventListener('resize', setViewportHeight);
    window.addEventListener('orientationchange', setViewportHeight);

    return () => {
      document.removeEventListener('touchstart', preventDefault);
      document.removeEventListener('touchmove', preventDefault);
      window.removeEventListener('resize', setViewportHeight);
      window.removeEventListener('orientationchange', setViewportHeight);
    };
  }, []);
  const [contacts, setContacts] = useState<MCPServer[]>([]);
  const [selectedId, setSelectedId] = useState<string>("");
  const [threads, setThreads] = useState<Record<string, ChatMessage[]>>(MOCK_THREADS);
  const [activePanel, setActivePanel] = useState<"contacts" | "threads" | "chat">("contacts");
  const [serverThreads, setServerThreads] = useState<Thread[]>([]);
  const [selectedThreadId, setSelectedThreadId] = useState<string | undefined>();
  const [showSettings, setShowSettings] = useState(false);
  const [settingsTarget, setSettingsTarget] = useState<MCPServer | null>(null);
  const [profileSettings, setProfileSettings] = useState<ProfileSettings | null>(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [accountDefaultTab, setAccountDefaultTab] = useState<TabKey>("account");
  const [showServerSearch, setShowServerSearch] = useState(false);
  const { showExitIntent, closeExitIntent } = useExitIntent();
  const [llmSettings, setLlmSettings] = useState(defaultLLMSettings);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [showWishlistModal, setShowWishlistModal] = useState(false);
  const [showNewsletterModal, setShowNewsletterModal] = useState(false);
  const [route, setRoute] = useState<string>(typeof window !== "undefined" ? window.location.hash : "");
  const mcpClient = useMemo(() => new MCPClient(), []);

  useEffect(() => {
    const handler = () => setRoute(window.location.hash);
    window.addEventListener("hashchange", handler);
    return () => window.removeEventListener("hashchange", handler);
  }, []);

  useEffect(() => {
    const stored = loadServers();
    if (stored && stored.length) {
      setContacts(stored);
      setSelectedId(stored[0]?.id ?? "");
    } else {
      setContacts(MOCK_SERVERS);
      setSelectedId(MOCK_SERVERS[0]?.id ?? "");
    }
  }, []);

  useEffect(() => {
    if (!contacts.length) return;
    saveServers(contacts);
    (async () => {
      const entries = await Promise.all(
        contacts.map(async contact => [contact.id, await loadThread(contact.id)] as [string, ChatMessage[]]),
      );
      const persisted = Object.fromEntries(entries.filter(([, msgs]) => msgs.length > 0));
      if (Object.keys(persisted).length) {
        setThreads(prev => ({ ...prev, ...persisted }));
      }
    })();
  }, [contacts]);

  useEffect(() => {
    (async () => {
      const profile = await loadProfileSettings();
      setProfileSettings(profile);
    })();
  }, []);

  useEffect(() => {
    const stored = loadLLMSettings();
    setLlmSettings(stored ?? defaultLLMSettings);
  }, []);

  const selectedContact = useMemo(() => contacts.find(contact => contact.id === selectedId), [contacts, selectedId]);
  const messages = selectedThreadId 
    ? (threads[selectedThreadId] ?? [])
    : (threads[selectedId] ?? []);

  const handleSelect = (id: string) => {
    setSelectedId(id);
    setSelectedThreadId(undefined);
    setActivePanel("threads");
    // Load threads for the selected server
    loadThreads(id).then(setServerThreads);
  };

  const handleSelectThread = (threadId: string) => {
    setSelectedThreadId(threadId);
    setActivePanel("chat");
    // Load messages for the thread
    loadThreadMessages(threadId).then(msgs => {
      setThreads(prev => ({ ...prev, [threadId]: msgs }));
    });
  };

  const handleNewThread = async () => {
    if (!selectedContact) return;
    
    const newThread = await createThread(selectedContact.id);
    setServerThreads(prev => [newThread, ...prev]);
    setSelectedThreadId(newThread.id);
    setThreads(prev => ({ ...prev, [newThread.id]: [] }));
    setActivePanel("chat");
  };

  // To stabilize the persistThread function reference (to avoid useEffect/useCallback loops - "maximum update depth exceeded" error), we wrap it in useCallback
  const persistThread = useCallback(
    (contactId: string, messagesToSave: ChatMessage[]) =>
      saveThread(contactId, messagesToSave).catch(error => console.error("Failed to persist thread", error)),
    [],
  );

  const handleThreadChange = useCallback(
    (contactId: string, messagesToSave: ChatMessage[]) => {
      const threadId = selectedThreadId || contactId;
      setThreads(prev => ({ ...prev, [threadId]: messagesToSave }));
      
      if (selectedThreadId) {
        // Save to specific thread
        saveThreadMessages(selectedThreadId, messagesToSave).catch(error => 
          console.error("Failed to persist thread messages", error)
        );
        
        // Update thread metadata with last message preview
        if (messagesToSave.length > 0) {
          const lastMessage = messagesToSave[messagesToSave.length - 1]!;
          const preview = lastMessage.content.slice(0, 100); // First 100 chars
          updateThread(selectedThreadId, { lastMessagePreview: preview })
            .then(() => {
              // Reload threads to show updated preview
              if (selectedContact) {
                loadThreads(selectedContact.id).then(setServerThreads);
              }
            })
            .catch(error => console.error("Failed to update thread metadata", error));
        }
      } else {
        // Legacy: save by contact ID
        persistThread(contactId, messagesToSave);
      }
    },
    [persistThread, selectedThreadId, selectedContact],
  );

  const handleSaveServer = (updated: MCPServer) => {
    setContacts(prev => {
      const exists = prev.some(server => server.id === updated.id);
      const next = exists ? prev.map(server => (server.id === updated.id ? { ...server, ...updated } : server)) : [...prev, updated];
      return next;
    });
    // @note - Don't close the panel here - let the ServerSettingsPanel control when to close
    // so the connect prompt can be shown
    // setShowSettings(false);
    // setSettingsTarget(null);
  };

  const handleDeleteServer = (server: MCPServer) => {
    setContacts(prev => prev.filter(item => item.id !== server.id));
    setShowSettings(false);
    setSettingsTarget(null);
    if (selectedId === server.id && contacts.length > 1) {
      const nextId = contacts.find(item => item.id !== server.id)?.id ?? "";
      setSelectedId(nextId);
    }
  };

  const handleSaveLlmSettings = (settings: LLMSettings) => {
    setLlmSettings(settings);
    saveLLMSettings(settings);
  };

  const handleRefreshConnection = useCallback(
    async (contact: MCPServer) => {
      setContacts(prev => prev.map(server => (server.id === contact.id ? { ...server, status: "connecting" } : server)));
      try {
        const { success, message }: { success: boolean; message?: string } = await mcpClient.ping(contact);
        setContacts(prev => prev.map(server => (server.id === contact.id ? { ...server, status: success ? "online" : "offline" } : server)));
        const statusMessage = success
          ? `${contact.name} is reachable.`
          : `${contact.name} is unreachable (${message}). Check network/VPN or MCP URL and try again.`;
        if (success) {
          toast.success(statusMessage);
        } else {
          toast.error(statusMessage);
        }
      } catch (error: any) {
        setContacts(prev => prev.map(server => (server.id === contact.id ? { ...server, status: "offline" } : server)));
        toast.error(`Failed to reach ${contact.name}. ${error?.message ?? ""}`);
      }
    },
    [mcpClient],
  );

  if (route === "#/dev") {
    return <DbManager />;
  }

  return (
    <>
      <AppShell
        activePanel={activePanel}
        onBack={() => {
          if (activePanel === "chat") {
            setActivePanel("threads");
          } else if (activePanel === "threads") {
            setActivePanel("contacts");
          }
        }}
        contactsPanel={
          <ContactList
            contacts={contacts}
            selectedId={selectedId}
            onSelect={handleSelect}
            onAdd={() => {
              setSettingsTarget(null);
              setShowSettings(true);
            }}
            onEdit={contact => {
              setSettingsTarget(contact);
              setShowSettings(true);
            }}
            onDelete={contact => handleDeleteServer(contact)}
            onSearch={() => setShowServerSearch(true)}
            onRefresh={handleRefreshConnection}
          />
        }
        threadsPanel={
          <ThreadList
            threads={serverThreads}
            selectedThreadId={selectedThreadId}
            server={selectedContact}
            servers={contacts}
            onSelectThread={handleSelectThread}
            onNewThread={handleNewThread}
          />
        }
        chatPanel={
          USE_HAPIMCP ? (<ChatWindowTest
            key={selectedThreadId ?? selectedContact?.id ?? "no-contact"}
            contact={selectedContact!}
            llmSettings={llmSettings}
          />) :
          (<ChatWindow
            key={selectedThreadId ?? selectedContact?.id ?? "no-contact"}
            contact={selectedContact}
            threadId={selectedThreadId}
            initialMessages={messages}
            onMessagesChange={updated => {
              if (selectedContact) {
                handleThreadChange(selectedContact.id, updated);
              }
            }}
            llmSettings={llmSettings}
            onLlmSettingsChange={handleSaveLlmSettings}
            onServerStatusChange={(id, status) => {
              setContacts(prev => prev.map(server => (server.id === id ? { ...server, status } : server)));
            }}
            onOpenSettings={() => {
              setSettingsTarget(selectedContact ?? null);
              setShowSettings(true);
            }}
            onOpenAccount={() => {
              setAccountDefaultTab("account");
              setShowAccountModal(true);
            }}
          />)
        }
        onOpenProfile={() => setShowProfileModal(true)}
        onOpenAccountTab={tab => {
          setAccountDefaultTab(tab as TabKey);
          setShowAccountModal(true);
        }}
        onOpenDemo={() => setShowDemoModal(true)}
        onOpenWishlist={() => setShowWishlistModal(true)}
        onOpenNewsletter={() => setShowNewsletterModal(true)}
      />
      {showSettings ? (
        <ServerSettingsPanel
          server={settingsTarget}
          onClose={() => setShowSettings(false)}
          onSave={handleSaveServer}
          onDelete={handleDeleteServer}
        />
      ) : null}
      {showProfileModal && profileSettings ? (
        <ProfileSettingsPanel
          initial={profileSettings}
          onClose={() => setShowProfileModal(false)}
          onSave={settings => {
            setProfileSettings(settings);
            saveProfileSettings(settings);
          }}
          onActivate={() => {
            setShowProfileModal(false);
            setShowAccountModal(true);
          }}
        />
      ) : null}
      {showAccountModal ? (
        <AccountSettingsPanel
          onClose={() => setShowAccountModal(false)}
          llmSettings={llmSettings}
          onLlmSettingsChange={handleSaveLlmSettings}
          defaultTab={accountDefaultTab}
        />
      ) : null}
      {showServerSearch ? <MCPServerSearchModal onClose={() => setShowServerSearch(false)} llmSettings={llmSettings} onLlmSettingsChange={handleSaveLlmSettings} /> : null}
      <DemoRequestModal open={showDemoModal} onClose={() => setShowDemoModal(false)} />
      <WishlistModal open={showWishlistModal} onClose={() => setShowWishlistModal(false)} />
      <NewsletterModal open={showNewsletterModal} onClose={() => setShowNewsletterModal(false)} />
      <ExitIntentModal open={showExitIntent} onClose={closeExitIntent} />
      <Toaster />
    </>
  );
}

export default App;
if (import.meta.hot) {
  import.meta.hot.accept()
}
